import { AlertTriangle, Check, CheckCircle2 } from 'lucide-react';
import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { InvoiceLineItemValidation } from 'src/redux/billing/billing.types';
import LineItemValidationInvalidSummary from './line-item-validation-invalid-summary';
import type { LineItemFieldValidationOutcome } from '../../utils/billing-utils';

interface StatusBadgeProps {
  status: string;
  attention_needed?: boolean;
  /**
   * When set (line item drawer), replaces the status-coloured dot with validation / approval icons.
   * Omitted elsewhere — original dot behaviour is preserved.
   */
  fieldValidationOutcome?: LineItemFieldValidationOutcome;
  /** Active validations for invalid popover (drawer line items). */
  lineItemValidations?: InvoiceLineItemValidation[];
  /** Enables Apply / Ignore in the invalid popover (drawer line items). */
  lineItemId?: string;
  invoiceGroupId?: string;
  isLocked?: boolean;
  updateLineItem?: (lineItemId: string, field: string, value: unknown) => Promise<void>;
  refetchInvoiceGroup?: () => void | Promise<void>;
}

const getStatusColor = (status: string, isDot: boolean = false) => {
  switch (status.toLowerCase()) {
    case 'draft':
      return isDot ? 'bg-gray-600' : 'text-black';
    case 'approved':
      return isDot ? 'bg-green-501' : 'text-black';
    case 'synced':
      return isDot ? 'bg-blue-500' : 'text-black';
    case 'paid':
      return isDot ? 'bg-purple-500' : 'text-black';
    case 'overdue':
      return isDot ? 'bg-yellow-500' : 'text-black';
    case 'pending_sync':
      return isDot ? 'bg-blue-300' : 'text-black';
    case 'sync_error':
      return isDot ? 'bg-red-502' : 'text-black';
    case 'needs_sync':
      return isDot ? 'bg-orange-501' : 'text-black';
    default:
      return isDot ? 'bg-gray-600' : 'text-black';
  }
};

const formatStatusText = (status: string) => {
  return status.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

/** Same width as the invalid warning button (`h-7 w-7`) so leading icons align across rows. */
const leadingSlotClassName = 'inline-flex h-7 w-7 shrink-0 items-center justify-center';

export const StatusBadgeThin: React.FC<StatusBadgeProps> = ({
  status,
  attention_needed = false,
  fieldValidationOutcome,
  lineItemValidations = [],
  lineItemId,
  invoiceGroupId,
  isLocked,
  updateLineItem,
  refetchInvoiceGroup,
}) => {
  const renderLeadingIndicator = () => {
    if (fieldValidationOutcome == null) {
      return (
        <span className={leadingSlotClassName}>
          <div className={`h-2 w-2 rounded-full ${getStatusColor(status, true)}`} />
        </span>
      );
    }

    if (fieldValidationOutcome === 'valid') {
      return (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={leadingSlotClassName}>
                <CheckCircle2
                  className="h-3.5 w-3.5 text-blue-600"
                  aria-hidden
                  strokeWidth={2}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                This line has been validated.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (fieldValidationOutcome === 'invalid') {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`${leadingSlotClassName} cursor-pointer rounded-full border border-transparent text-red-600 outline-none ring-offset-2 transition-colors hover:border-red-200 hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-400`}
              aria-label="This line failed validation. Click to see incorrect field and value."
              title="This line failed validation — click for details"
            >
              <AlertTriangle
                className="h-3.5 w-3.5 pointer-events-none"
                aria-hidden
                strokeWidth={2}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-72 max-w-[min(calc(100vw-1.5rem),18rem)] bg-white p-2.5 text-xs shadow-md"
          >
            <LineItemValidationInvalidSummary
              validations={lineItemValidations}
              lineItemId={lineItemId}
              invoiceGroupId={invoiceGroupId}
              isLocked={isLocked}
              updateLineItem={updateLineItem}
              refetchInvoiceGroup={refetchInvoiceGroup}
            />
          </PopoverContent>
        </Popover>
      );
    }

    // unknown — default to status dot, except approved → green check
    if (status.toLowerCase() === 'approved') {
      return (
        <span className={leadingSlotClassName}>
          <Check
            className="h-3.5 w-3.5 text-green-600"
            aria-hidden
            strokeWidth={2.5}
          />
        </span>
      );
    }

    return (
      <span className={leadingSlotClassName}>
        <div className={`h-2 w-2 rounded-full ${getStatusColor(status, true)}`} />
      </span>
    );
  };

  if (attention_needed && fieldValidationOutcome !== 'invalid') {
    return (
      <span className="inline-flex items-center justify-center gap-1 px-2 py-[1px] text-[15px] font-bold text-red-501">
        <span className={leadingSlotClassName}>
          <div className="h-2 w-2 rounded-full bg-red-501" />
        </span>
        Review
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center gap-1 px-2 py-[1px] text-[13px] font-bold ${getStatusColor(status)}`}
    >
      {renderLeadingIndicator()}
      {formatStatusText(status)}
    </span>
  );
};

export default StatusBadgeThin;
